import { useCallback } from "react";
import { useSetAtom, useAtomValue } from "jotai";
import { useApi } from "./useApi";
import { useSessionSetAtom } from "../useSessionSetAtom";
import { createReadGuard } from "./_helpers/readGuard";
import type {
    LocationResponse,
    LocationDetailResponse,
    CreateLocationRequest,
    CreateLocationResponse,
    UpdateLocationRequest,
    UpdateLocationAddressRequest,
    UpdateLocationAddressResponse,
    RejectLocationRequest,
} from "@/models/location";
import { fetchDownload, type DownloadedFile } from "@utils/fileDownload";
import { organizationsAtom } from "@/store/organizationAtoms";
import {
    locationsAtom,
    locationsLoadedAtom,
    isFetchingLocationsAtom,
    locationsFetchErrorAtom,
    isProcessingLocationsAtom,
} from "@/store/locationAtoms";

// Q6: certificates are PDF, JPG or PNG.
const CERTIFICATE_CONTENT_TYPES = ["application/pdf", "image/jpeg", "image/png"];

// Which GET /locations may publish to the shared location atoms (#407). Several
// surfaces read the list at once (the header switcher with Checkout, the order
// confirmation or an account page), and the header switcher derives its status
// from these atoms (shipToLocationsStatusAtom). Module-scope, like
// useShoppingCartApi's, and both checks for the same reason: `isLatest` owns
// the STATUS atoms (only the newest read may say fetching or failed), and
// `claimPublish` owns the LIST: a successful read publishes when nothing newer
// has already published. So an older answer can't replace a newer list, an
// older failure can't mark a read still in flight as failed, and a good list
// is not thrown away because the read that superseded it then failed (which
// would leave the switcher saying the list couldn't be loaded, and every page
// without it, when it had loaded).
const locationsReads = createReadGuard();

// The address-edit failures the Edit Location drawer words itself
// (Admin/Clients/_helpers/editLocation.ts): the 409s, the two coded
// certificate 400s, and a 404 the API authored (the location or its client is
// gone). A validation 400, a 404 without the API's body (a gateway page, a
// missing route) and a network failure keep the toast.
const ADDRESS_EDIT_INLINE_400_CODES = [
    "certificate_required",
    "certificate_unexpected",
];
const isAddressEditFailureExplainedInline = (
    status: number | undefined,
    errorCode: string | undefined,
    hasApiBody: boolean,
): boolean =>
    status === 409 ||
    (status === 404 && hasApiBody) ||
    (status === 400 &&
        errorCode !== undefined &&
        ADDRESS_EDIT_INLINE_400_CODES.includes(errorCode));

export const useLocationApi = () => {
    const { callApi } = useApi();
    // Session-bound: a session that ended while a read was on the wire has
    // already been cleared (endSessionAtom); its late answer must not refill it.
    const organizations = useAtomValue(organizationsAtom);
    const setOrganizations = useSessionSetAtom(organizationsAtom);
    const setLocations = useSessionSetAtom(locationsAtom);
    const setHasLoaded = useSessionSetAtom(locationsLoadedAtom);
    const setIsFetching = useSetAtom(isFetchingLocationsAtom);
    const setFetchError = useSessionSetAtom(locationsFetchErrorAtom);
    const setIsProcessing = useSetAtom(isProcessingLocationsAtom);

    const createLocation = useCallback(
        async (
            organizationId: string,
            data: CreateLocationRequest,
        ): Promise<CreateLocationResponse> => {
            setIsProcessing(true);

            try {
                const response = await callApi<CreateLocationResponse>(
                    `/organizations/${organizationId}/locations`,
                    {
                        method: "POST",
                        body: JSON.stringify(data),
                    },
                );

                const newLocation = {
                    id: response.id,
                    name: data.name,
                    isActive: true,
                    // Just created: nothing has been uploaded since a review.
                    hasPendingCertReview: false,
                };

                const updatedOrganizations = organizations.map((org) =>
                    org.id === organizationId
                        ? { ...org, locations: [...org.locations, newLocation] }
                        : org,
                );

                setOrganizations(updatedOrganizations);

                return response;
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setOrganizations, organizations, setIsProcessing],
    );

    const getLocations = useCallback(
        async (): Promise<LocationResponse[]> => {
            const { isLatest, claimPublish } = locationsReads.begin();

            setIsFetching(true);
            setFetchError(false);

            try {
                const data = await callApi<LocationResponse[]>(
                    '/locations',
                );

                if (claimPublish()) {
                    setLocations(data);
                    // "The list has been read", which locationsAtom's own [] cannot say — it is
                    // equally "never read" and "read, and you have none" (#228 / E4-S12).
                    setHasLoaded(true);
                }

                return data;
            } catch (error) {
                if (isLatest()) setFetchError(true);
                throw error;
            } finally {
                if (isLatest()) setIsFetching(false);
            }
        },
        [callApi, setLocations, setHasLoaded, setIsFetching, setFetchError],
    );

    // Admin per-location reject (#197 / contract B). Sets the location to
    // Rejected with a reason category (+ optional notes); does not touch org
    // status. The server 409s if the location is not Pending.
    const rejectLocation = useCallback(
        async (
            locationId: string,
            data: RejectLocationRequest,
        ): Promise<void> => {
            setIsProcessing(true);

            try {
                await callApi(`/locations/${locationId}/reject`, {
                    method: "POST",
                    body: JSON.stringify(data),
                });
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    // Customer per-location resubmit after a rejection (#198 / E5-S11). Posts a
    // fresh resale certificate; the server flips the location back to Pending.
    // Authenticated — the caller is the signed-in owner, not the anonymous
    // /apply form. The server 409s if the location is not Rejected.
    const resubmitLocation = useCallback(
        async (locationId: string, certificate: File): Promise<void> => {
            setIsProcessing(true);

            try {
                const formData = new FormData();

                // Name must match the command's [FromForm] body exactly — same
                // `Body.` prefix convention as useApplicationApi.
                formData.append("Body.ResaleCertificate", certificate);

                await callApi(`/locations/${locationId}/resubmit`, {
                    method: "POST",
                    body: formData,
                    contentType: "multipart",
                });
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    // Customer certificate upload for an APPROVED or PENDING location (E12-S5 /
    // #240, #437). The file is appended; status is untouched. An approved
    // location's raises the admin's "New cert" badge until acknowledged; a
    // pending one's is reviewed by its approval. Same multipart shape as
    // resubmitLocation. skipErrorNotification: the modal renders its own
    // inline, per-case message (409 not-eligible, 404, 400) — same posture as
    // approveOrganization.
    const uploadLocationCert = useCallback(
        async (locationId: string, certificate: File): Promise<void> => {
            setIsProcessing(true);

            try {
                const formData = new FormData();

                // Name must match UploadMeLocationCertCommandBody exactly
                // (`Body.` prefix, [FromForm]).
                formData.append("Body.ResaleCertificate", certificate);

                await callApi(`/locations/me/${locationId}/cert`, {
                    method: "POST",
                    body: formData,
                    contentType: "multipart",
                    skipErrorNotification: true,
                });
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    // Admin read of one location (E12-S5 / #240): its certificates, newest
    // first, and whether the newest still awaits review. No atom — the client
    // detail page reads one of these per location row, and a single-valued
    // atom would have the rows overwrite each other. skipErrorNotification:
    // the row renders its own inline failure with a Retry; one toast per row
    // would stack unattributed toasts on top of it.
    const getLocation = useCallback(
        async (locationId: string): Promise<LocationDetailResponse> =>
            callApi<LocationDetailResponse>(`/locations/${locationId}`, {
                skipErrorNotification: true,
            }),
        [callApi],
    );

    // Admin acknowledgement of a re-uploaded certificate (E12-S5 / #240):
    // clears the location's pending-review flag. The server 409s if the
    // location is not Approved. skipErrorNotification: the row renders its
    // own inline message per case (409 / 403 / 404).
    const acknowledgeLocationCert = useCallback(
        async (locationId: string): Promise<void> => {
            setIsProcessing(true);

            try {
                await callApi(`/locations/${locationId}/cert/acknowledge`, {
                    method: "POST",
                    skipErrorNotification: true,
                });
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    // Admin certificate download (E12-S5 / #240). The endpoint needs the
    // bearer token, so the bytes come back through callApi as a Blob and are
    // saved client-side — never linked directly. `fileName` is the server's
    // Content-Disposition name when that header is readable: the API only
    // exposes it cross-origin once PR 270 (#213) merges, so until then it is
    // unreadable, `fileName` is undefined, and the caller uses the name it
    // listed. "silent": the row shows a per-file inline failure, so no toast,
    // and the request's own rejection reaches it untouched. A 2xx that is not
    // a certificate (an empty body, a gateway's "session expired" page)
    // rejects as DownloadError instead of being saved as `cert-2026.pdf`.
    const downloadLocationFile = useCallback(
        (locationId: string, fileId: string): Promise<DownloadedFile> =>
            fetchDownload(callApi, `/locations/${locationId}/files/${fileId}`, {
                failures: "silent",
                acceptedTypes: CERTIFICATE_CONTENT_TYPES,
            }),
        [callApi],
    );

    const updateLocation = useCallback(
        async (
            locationId: string,
            data: UpdateLocationRequest,
        ): Promise<void> => {
            setIsProcessing(true);

            try {
                await callApi(
                    `/locations/${locationId}`,
                    {
                        method: "PUT",
                        body: JSON.stringify(data),
                    },
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    // A platform admin edits a client location's address (C6, API #433). The
    // updatedAt goes as read (never re-serialized through a Date), and is left
    // out when null, which the API binds as null. A 200 without a JSON body
    // carries no answer: useApi hands back its text instead, hence `| string`.
    const updateLocationAddress = useCallback(
        async (
            locationId: string,
            data: UpdateLocationAddressRequest,
        ): Promise<UpdateLocationAddressResponse | string> => {
            setIsProcessing(true);

            try {
                const formData = new FormData();

                // Names must match UpdateLocationAddressCommandBody exactly
                // (`Body.` prefix, [FromForm]).
                formData.append("Body.StreetAddress", data.streetAddress);
                formData.append("Body.City", data.city);
                formData.append("Body.State", data.state);
                formData.append("Body.ZipCode", data.zipCode);
                formData.append("Body.Country", data.country);
                if (data.updatedAt !== null) {
                    formData.append("Body.UpdatedAt", data.updatedAt);
                }
                if (data.resaleCertificate) {
                    formData.append(
                        "Body.ResaleCertificate",
                        data.resaleCertificate,
                    );
                }

                return await callApi<UpdateLocationAddressResponse | string>(
                    `/locations/${locationId}/address`,
                    {
                        method: "PUT",
                        body: formData,
                        contentType: "multipart",
                        skipErrorNotification:
                            isAddressEditFailureExplainedInline,
                    },
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    const deleteLocation = useCallback(
        async (locationId: string): Promise<void> => {
            setIsProcessing(true);

            try {
                await callApi(
                    `/locations/${locationId}`,
                    {
                        method: "DELETE",
                    },
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    return {
        createLocation,
        getLocations,
        rejectLocation,
        resubmitLocation,
        uploadLocationCert,
        getLocation,
        acknowledgeLocationCert,
        downloadLocationFile,
        updateLocation,
        updateLocationAddress,
        deleteLocation,
    };
};
